import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, Clock, MapPin, ShieldCheck, ChevronRight, Download, Send, Globe, Building } from 'lucide-react';

export default function Footer() {
  const currentYear = new Date().getFullYear();

  const handleSubscribe = (e) => {
    e.preventDefault();
    alert('Thank you for subscribing to our newsletter!');
    e.target.reset();
  };

  return (
    <footer className="bg-navy border-t border-white/10 text-gray-400 font-sans">
      {/* Top Banner - Security Badges */}
      <div className="bg-navy-dark py-6 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-6 text-center md:text-left">
          <div className="flex flex-col md:flex-row items-center justify-center md:justify-start space-y-2 md:space-y-0 md:space-x-4">
            <div className="p-3 bg-primary/10 text-golden rounded-2xl">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div>
              <h4 className="text-white font-montserrat font-semibold text-sm">RBI Compliant Institution</h4>
              <p className="text-xs text-gray-500">Adhering strictly to co-operative banking guidelines</p>
            </div>
          </div>
          <div className="flex flex-col md:flex-row items-center justify-center md:justify-start space-y-2 md:space-y-0 md:space-x-4 border-y md:border-y-0 md:border-x border-white/5 py-4 md:py-0 md:px-6">
            <div className="p-3 bg-primary/10 text-golden rounded-2xl">
              <Globe className="h-6 w-6" />
            </div>
            <div>
              <h4 className="text-white font-montserrat font-semibold text-sm">NPCI & IMPS Partner</h4>
              <p className="text-xs text-gray-500">Enabling fast, secure digital transactions 24/7</p>
            </div>
          </div>
          <div className="flex flex-col md:flex-row items-center justify-center md:justify-start space-y-2 md:space-y-0 md:space-x-4">
            <div className="p-3 bg-primary/10 text-golden rounded-2xl">
              <Phone className="h-6 w-6" />
            </div>
            <div>
              <h4 className="text-white font-montserrat font-semibold text-sm">24/7 Help Desk</h4>
              <p className="text-xs text-gray-500">Call us at 1800-123-4567 for emergency blockages</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Links */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8">
        {/* Info Column */}
        <div className="space-y-4 sm:col-span-2 lg:col-span-2">
          <Link to="/" className="inline-block bg-white p-2.5 rounded-2xl shadow-md">
            <img src="/Sahyadri Urban Logo.png" alt="Sahyadri Urban Logo" className="h-10 w-auto object-contain" />
          </Link>
          <p className="text-sm leading-relaxed pr-4">
            Established with a commitment to empower families, farmers, and business owners. Serving the community with premium financial returns, flexible financing solutions, and secure banking portals.
          </p>
          <div className="pt-2 space-y-2">
            <div className="flex items-start space-x-3 text-sm">
              <MapPin className="h-5 w-5 text-golden shrink-0 mt-0.5" />
              <span>Plot No. 42, Sahyadri Bhavan, Cooperative Plaza, Sangli Road, Sangli - 415001, Maharashtra, India</span>
            </div>
            <div className="flex items-center space-x-3 text-sm">
              <Phone className="h-5 w-5 text-golden shrink-0" />
              <span>+91 2162 234567 | +91 98765 43210</span>
            </div>
            <div className="flex items-center space-x-3 text-sm">
              <Mail className="h-5 w-5 text-golden shrink-0" />
              <span>info@sahyadriurban.com</span>
            </div>
          </div>
        </div>

        {/* Banking Schemes */}
        <div>
          <h3 className="text-white font-montserrat font-bold text-sm uppercase tracking-wider mb-5">Deposit Schemes</h3>
          <ul className="space-y-3 text-sm">
            <li><Link to="/deposits#savings-dep" className="hover:text-white transition-colors flex items-center"><ChevronRight className="h-3 w-3 mr-1.5 text-golden" /> Savings Deposit Account</Link></li>
            <li><Link to="/current-account" className="hover:text-white transition-colors flex items-center"><ChevronRight className="h-3 w-3 mr-1.5 text-golden" /> Current Account</Link></li>
            <li><Link to="/deposits#fixed" className="hover:text-white transition-colors flex items-center"><ChevronRight className="h-3 w-3 mr-1.5 text-golden" /> Fixed Deposit (FD)</Link></li>
            <li><Link to="/deposits#recurring" className="hover:text-white transition-colors flex items-center"><ChevronRight className="h-3 w-3 mr-1.5 text-golden" /> Recurring Deposit (RD)</Link></li>
            <li><Link to="/deposits#mis" className="hover:text-white transition-colors flex items-center"><ChevronRight className="h-3 w-3 mr-1.5 text-golden" /> Monthly Income Scheme</Link></li>
            <li><Link to="/deposits#senior" className="hover:text-white transition-colors flex items-center"><ChevronRight className="h-3 w-3 mr-1.5 text-golden" /> Senior Citizen Scheme</Link></li>
            <li><Link to="/deposits#women" className="hover:text-white transition-colors flex items-center"><ChevronRight className="h-3 w-3 mr-1.5 text-golden" /> Women Savings Scheme</Link></li>
          </ul>
        </div>

        {/* Loan Products */}
        <div>
          <h3 className="text-white font-montserrat font-bold text-sm uppercase tracking-wider mb-5">Loan Products</h3>
          <ul className="space-y-3 text-sm">
            <li><Link to="/loans#personal" className="hover:text-white transition-colors flex items-center"><ChevronRight className="h-3 w-3 mr-1.5 text-golden" /> Personal Loan</Link></li>
            <li><Link to="/loans#home" className="hover:text-white transition-colors flex items-center"><ChevronRight className="h-3 w-3 mr-1.5 text-golden" /> Home Loan</Link></li>
            <li><Link to="/loans#gold" className="hover:text-white transition-colors flex items-center"><ChevronRight className="h-3 w-3 mr-1.5 text-golden" /> Gold Loan</Link></li>
            <li><Link to="/loans#vehicle" className="hover:text-white transition-colors flex items-center"><ChevronRight className="h-3 w-3 mr-1.5 text-golden" /> Vehicle Loan</Link></li>
            <li><Link to="/loans#business" className="hover:text-white transition-colors flex items-center"><ChevronRight className="h-3 w-3 mr-1.5 text-golden" /> Business Loan</Link></li>
            <li><Link to="/loans#agri" className="hover:text-white transition-colors flex items-center"><ChevronRight className="h-3 w-3 mr-1.5 text-golden" /> Agriculture Loan</Link></li>
          </ul>
        </div>

        {/* Downloads & Corporate */}
        <div>
          <h3 className="text-white font-montserrat font-bold text-sm uppercase tracking-wider mb-5">Downloads & Center</h3>
          <ul className="space-y-3 text-sm">
            <li><a href="#" className="hover:text-white transition-colors flex items-center"><Download className="h-3.5 w-3.5 mr-1.5 text-golden" /> Account Opening Form</a></li>
            <li><a href="#" className="hover:text-white transition-colors flex items-center"><Download className="h-3.5 w-3.5 mr-1.5 text-golden" /> Loan Application Form</a></li>
            <li><a href="#" className="hover:text-white transition-colors flex items-center"><Download className="h-3.5 w-3.5 mr-1.5 text-golden" /> KYC Upgrade Form</a></li>
            <li><a href="#" className="hover:text-white transition-colors flex items-center"><Download className="h-3.5 w-3.5 mr-1.5 text-golden" /> Annual Report 2025-26</a></li>
            <li><a href="#" className="hover:text-white transition-colors flex items-center"><Download className="h-3.5 w-3.5 mr-1.5 text-golden" /> Interest Rates Chart</a></li>
          </ul>
        </div>
      </div>

      {/* Middle Segment - Business Hours, IFSC & Newsletter */}
      <div className="border-t border-white/5 py-12 bg-navy-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Business Hours */}
          <div className="flex items-start space-x-3">
            <Clock className="h-5 w-5 text-golden shrink-0 mt-0.5" />
            <div>
              <h4 className="text-white font-montserrat font-semibold text-sm">Working Hours</h4>
              <p className="text-sm mt-1">Monday - Saturday: 10:00 AM - 5:00 PM</p>
              <p className="text-xs text-gray-500 mt-0.5">Closed on Sundays, 2nd & 4th Saturdays, and Bank Holidays</p>
            </div>
          </div>

          {/* Central Office IFSC details */}
          <div className="flex items-start space-x-3">
            <Building className="h-5 w-5 text-golden shrink-0 mt-0.5" />
            <div>
              <h4 className="text-white font-montserrat font-semibold text-sm">IFSC Details</h4>
              <p className="text-sm mt-1">Settlement Route: ICIC0000007 (ICICI Bank Agent Route)</p>
              <p className="text-xs text-gray-500 mt-0.5">Please check specific branch code details on Branches page.</p>
            </div>
          </div>

          {/* Newsletter subscription */}
          <div>
            <h4 className="text-white font-montserrat font-semibold text-sm">Subscribe to Financial Alerts</h4>
            <form onSubmit={handleSubscribe} className="mt-3 flex">
              <input
                type="email"
                required
                placeholder="Enter email address"
                className="w-full bg-navy border border-white/10 rounded-l-xl px-4 py-2 text-sm text-white focus:outline-none focus:border-golden transition-colors"
              />
              <button
                type="submit"
                className="bg-golden text-navy hover:bg-golden-dark transition-all rounded-r-xl px-4 flex items-center justify-center font-semibold"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Bottom Copyright segment */}
      <div className="border-t border-white/5 bg-[#050E1A] py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center text-xs space-y-4 md:space-y-0">
          <div>
            <p>&copy; {currentYear} Sahyadri Urban Co-operative Credit Society Ltd. All Rights Reserved. Reg No. STR/MH-789-2005.</p>
          </div>
          <div className="flex space-x-6">
            <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
            <a href="#" className="hover:text-white transition-colors">Cyber Security Policy</a>
            <a href="#" className="hover:text-white transition-colors">Disclaimer</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
